<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { ImageSingleUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let files = [];
  let inline = false;
  let fileSizeWarning = false;
  let fileData = {};

  const onFileChange = () => {
    fileSizeWarning = files.length > 0 && files[0].size > 3 * 1024 * 1024;
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
    fileSizeWarning = false;
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');
      throw new Error($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }));
    }

    const { hash, filesize, filename, metadata } = response;

    fileData = { ...fileData, hash, filesize, filename, metadata };
  };

  const executeCreateImage = async () => {
    $appStore.loading = true;

    await onFileUpload();

    const data = {
      filename: fileData.filename,
      filesize: fileData.filesize,
      hash: fileData.hash,
      inline: inline,
      metadata: fileData.metadata,
      mimetype: fileData.metadata.mimetype,
      rulename: fileData.filename
    };

    const response = await AssetsService.addImage(data);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: fileData.filename } }), 'error');
      throw new Error($format('label.FAILURE.CREATE_IMAGE'));
    }

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: fileData.filename } }));

    step.data = {
      id: fileData.filename
    };

    return response;
  };

  const rollbackCreateImage = async () => {
    if (!step.data?.id) {
      return;
    }

    $appStore.loading = true;

    try {
      const images = await AssetsService.getAllImages();
      const image = images.find((img) => img.filename === step.data.id);

      if (image) {
        await AssetsService.deleteImage(image);
      }
    } catch (error) {
      console.error('Failed to rollback image creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    if (onExecute) {
      onExecute(executeCreateImage);
    }
    if (onRollback) {
      onRollback(rollbackCreateImage);
    }
  });

  $: nextDisabled = !files.length;
</script>

<div class="image-single-create-form">
  <ImageSingleUploadForm
    bind:files
    bind:inline
    bind:fileSizeWarning
    {onFileChange}
    {onRemoveFile}
    inputId="workflow-image-single-create"
  />
</div>

<style lang="scss">
  .image-single-create-form {
    width: 100%;
  }
</style>
